import { observable, action, makeObservable } from 'mobx';

export type WsEventType =
  | 'taskPreview' | 'progress' | 'image_queued' | 'queue_position' | 'interrupted' | 'executing'
  // Chat events from server
  | 'chat:chunk' | 'chat:message_ids' | 'chat:new_assistant'
  | 'chat:tool_start' | 'chat:tool_progress' | 'chat:tool_done' | 'chat:tool_approval'
  | 'chat:done' | 'chat:error' | 'chat:restore_state' | 'chat:name_generated'
  | 'chat:compact_start' | 'chat:compact_done' | 'chat:messages_removed'
  // Steering (upcoming) messages
  | 'chat:upcoming' | 'chat:upcoming_taken' | 'chat:upcoming_removed'
  // Task plan (agent todo list) events
  | 'plan:updated' | 'plan:cleared'
  // Server live stats (from proxy socket stream)
  | 'server:stats'
  // Server live task list (running/queued, with timing + token counters) — admin only
  | 'server:tasks'
  // Local (localhost) MCP server relay over WebSocket
  | 'mcp:relay_request'
  // Local client-side events
  | 'ws:disconnect';

class WsService {
  private socket: WebSocket | null = null;
  private _reconnecting = false;
  @observable connectionState: 'connected' | 'connecting' | 'disconnected' = 'connecting';

  /** Unique ID for this browser tab – persisted in sessionStorage so each tab has its own ID */
  readonly clientId: string = (() => {
    let id = sessionStorage.getItem('ws_client_id');
    if (!id) { id = crypto.randomUUID(); sessionStorage.setItem('ws_client_id', id); }
    return id;
  })();

  constructor() {
    makeObservable(this);
  }

  @action
  connect() {
    if (this.socket) {
      this.socket.close();
    }
    this.connectionState = 'connecting';
    this.socket = new WebSocket('/ws');

    this.socket.onopen = action(() => {
      console.log('WebSocket connection established');
      this._reconnecting = false;
      this.connectionState = 'connected';
      // On reconnect, ask server for any running chat state
      this.send('chat:restore', {});
    });

    this.socket.onmessage = (event) => {
      //raise event
      if (typeof event.data !== 'string') {
        this.eventListeners['taskPreview']?.forEach(callback => callback(event.data));
        return;
      } else {
        const jsonData = JSON.parse(event.data);
        this.eventListeners[jsonData.type]?.forEach(callback => callback(jsonData.data));
      }
    };

    this.socket.onclose = action(() => {
      console.log('WebSocket connection closed');
      this.socket = null;
      this.connectionState = 'disconnected';
      // Notify listeners so active generations can show an error
      this.eventListeners['ws:disconnect']?.forEach(cb => cb({}));
      this._scheduleReconnect();
    });

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  private _scheduleReconnect() {
    if (this._reconnecting) return;
    this._reconnecting = true;
    setTimeout(action(() => {
      console.log('Attempting to reconnect WebSocket...');
      this._reconnecting = false;
      this.connectionState = 'connecting';
      this.connect();
    }), 3000);
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** Send a typed message to the server */
  send(type: string, data: unknown) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type, data }));
    } else {
      console.warn(`[wsService] Cannot send "${type}" - socket not connected`);
    }
  }

  private eventListeners: { [key: string]: ((data: unknown) => void)[] } = {};

  on(event: WsEventType, callback: (data: unknown) => void) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  off(event: WsEventType, callback: (data: unknown) => void) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }
}

export const wsService = new WsService();

wsService.connect();